import { type CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js/max'
import { z } from 'zod'

export const DEFAULT_PHONE_COUNTRY: CountryCode = 'LB'

export interface StoredPhone {
    country: CountryCode
    national: string
}

function normalizeInternationalPrefix(value: string): string {
    const compact = value.replace(/[^\d+]/g, '')
    return compact.startsWith('00') ? '+' + compact.slice(2) : compact
}

export function buildE164(national: string, country: CountryCode): string {
    const compact = normalizeInternationalPrefix(national)
    if (!compact) return ''
    if (compact.startsWith('+')) {
        return parsePhoneNumberFromString(compact)?.number ?? compact
    }
    return parsePhoneNumberFromString(compact, country)?.number ?? compact
}

// Legacy rows hold digits with no "+", and some hold Lebanese local numbers with no country code at all.
export function parseStoredPhone(value: string | null | undefined): StoredPhone | null {
    const compact = normalizeInternationalPrefix(value ?? '')
    const digits = compact.replace(/\D/g, '')
    if (!digits) return null

    const international = parsePhoneNumberFromString('+' + digits)
    if (international?.country && international.isValid()) {
        return { country: international.country, national: international.nationalNumber }
    }

    const local = parsePhoneNumberFromString(digits, DEFAULT_PHONE_COUNTRY)
    return { country: DEFAULT_PHONE_COUNTRY, national: local?.nationalNumber ?? digits.replace(/^0+/, '') }
}

export function isValidPhone(e164: string | null | undefined, options?: { mobileOnly?: boolean }): boolean {
    if (!e164?.startsWith('+')) return false
    const phone = parsePhoneNumberFromString(e164)
    if (!phone?.isValid()) return false
    if (!options?.mobileOnly) return true
    const type = phone.getType()
    return type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE'
}

export function phoneDigits(e164: string | null | undefined): string {
    return (e164 ?? '').replace(/\D/g, '')
}

export function phoneSchema(options?: { required?: boolean; mobileOnly?: boolean }) {
    const message = options?.mobileOnly ? 'Enter a valid mobile number' : 'Enter a valid phone number'
    return z.string().optional().superRefine((value, ctx) => {
        if (!value) {
            if (options?.required) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Phone number is required' })
            return
        }
        if (!isValidPhone(value, options)) ctx.addIssue({ code: z.ZodIssueCode.custom, message })
    })
}

export function normalizeStoredPhone(value: string | null | undefined): string {
    const stored = parseStoredPhone(value)
    return stored ? buildE164(stored.national, stored.country) : ''
}
